import { Inject, Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { AuthConfig, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { OAuthService } from 'angular-oauth2-oidc';
import { take } from 'rxjs/operators';
import { GenericResponse, UserInfo } from '../interfaces';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../commons/config/config.service';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { DOCUMENT } from '@angular/common';
import { UtilService } from '@services/util.service';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalChooseCountryComponent } from '@shared/modal-choose-country/modal-choose-country.component';
import { UserManagementService } from '../modules/user-management/services/user-management.service';

export type IAppRoles = '300000001' | '300000002' | '300000003' | '300000004' | '300000005' | '300000006' | '300000007';
const allowedRoles = [
  { code: '300000001', value: 'USER_MANAGEMENT.CHANNEL_MANGER_ACCIONA', suffix: '-large-accounts' }, // GESTOR ACCIONA
  { code: '300000002', value: 'USER_MANAGEMENT.CHANNEL_COMMERCIAL_ACCIONA', suffix: '-large-accounts' }, // COMERCIAL ACCIONA
  { code: '300000003', value: 'USER_MANAGEMENT.CHANNEL_MANAGER', suffix: '-pymes' }, // GESTOR CANAL
  { code: '300000004', value: 'USER_MANAGEMENT.CHANNEL_COMMERCIAL', suffix: '-pymes' }, // COMERCIAL CANAL
  { code: '300000005', value: 'USER_MANAGEMENT.CHANNEL_BACKOFFICE', suffix: '-pymes' }, // BACK OFFICE'
  { code: '300000006', value: 'USER_MANAGEMENT.CHANNEL_MASTER', suffix: '-pymes' }, // MASTER
  { code: '300000007', value: 'USER_MANAGEMENT.CHANNEL_ACCIONA', suffix: '-pymes' } // ADMIN ACCIONA
];
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public isLoading: Subject<boolean> = new Subject<boolean>();
  public hasError: Subject<boolean> = new Subject<boolean>();
  public userData: GenericResponse<UserInfo>;
  public ready = new BehaviorSubject<boolean>(false);

  public constructor(
    private oauthService: OAuthService,
    private http: HttpClient,
    private config: ConfigService,
    private utilService: UtilService,
    private dateAdapter: DateAdapter<any>,
    private dialog: MatDialog,
    private userManagementService: UserManagementService,
    @Inject(MAT_DATE_LOCALE) private dateLocale: string,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
    @Inject(DOCUMENT) protected document: Document
  ) { }

  public get token(): string {
    return this.oauthService.getAccessToken();
  }

  public getUserData(): UserInfo {
    return this.userData?.data;
  }

  public getRole() {
    if (this.userData) {
      const intersect = this.userData.data.roles.filter(value =>
        allowedRoles.find(item => item.code === value)
      ) as IAppRoles[];
      const role = allowedRoles.find(item => item.code === intersect[ 0 ]);
      return role;
    }
  }

  public getOnlyCanRead(): boolean {
    // for large account users dont have read-only permissions
    return this.getRole().suffix === '-pymes' ? this.userData.data.soloLectura : false;
  }

  public login() {
    return this.http.get<GenericResponse<UserInfo[]>>(this.config.getUrl('login'));
  }

  public configure(authConfig: AuthConfig) {
    this.isLoading.next(true);
    this.hasError.next(false);
    this.oauthService.setStorage(sessionStorage);
    this.oauthService.configure(authConfig);
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (this.oauthService.hasValidAccessToken()) {
        this.login().pipe(take(1)).subscribe(res => {
          sessionStorage.setItem('num_countries', res?.data?.length.toString());
          if (res?.data?.length > 1) {
            this.openChooseCountryDialog(res);
          } else {
            const userLogin = sessionStorage.getItem('country') !== null;
            const data = { ...res, data: res.data[0] };
            sessionStorage.setItem('country', data.data.country);
            if (!userLogin) {
              this.setLastAccess(data);
            } else {
              this.setUserData(data);
            }
          }
        }, error => {
          error.message = ' Usuario o contraseña incorrecto';
          this.utilService.toastErrors(error);
          this.isLoading.next(false);
        });
      } else {
        this.oauthService.initCodeFlow();
      }
    });

    this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        if (event.type !== 'silent_refresh_timeout') {
          this.hasError.next(true);
        }
      }
    });
  }

  private setUserData(userData: GenericResponse<UserInfo>): void {
    this.isLoading.next(false);
    this.userData = userData;
    this.setUserLang();
    this.ready.next(true);
  }

  private setLastAccess(userInfo: GenericResponse<UserInfo>): void {
    this.userManagementService.lastAccess().pipe(take(1)).subscribe(
      res => {
        if (res.success) {
          this.setUserData(userInfo);
        } else {
          this.utilService.toastErrors(res);
          this.isLoading.next(false);
        }
      },
      error => {
        this.utilService.toastErrors(error);
        this.isLoading.next(false);
      }
    );
  }

  private openChooseCountryDialog(res: GenericResponse<UserInfo[]>): void {
    const country = sessionStorage.getItem('country');
    if (!country) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        autoFocus: false,
        disableClose: true
      };
      const dialogRef = this.dialog.open(ModalChooseCountryComponent, dialogConfig);
      dialogRef.afterClosed().pipe(take(1)).subscribe((result: string) => {
        if (result) {
          const userIndex = res.data.findIndex(item => item.country === result);
          const data = { ...res, data: res.data[userIndex] };
          this.setUserData(data);
        }
      });
    } else {
      const userIndex = res.data.findIndex(item => item.country === country);
      const data = { ...res, data: res.data[userIndex] };
      this.setUserData(data);
    }
  }

  public logout(): void {
    this.oauthService.logOut(false);
  }

  private setUserLang(): void {
    const langSession = sessionStorage.getItem('i18nlang');
    const language = this.userData.data.country === 'ES' ? 'es-ES' : 'pt-PT';
    if (langSession) {
      document.documentElement.lang = langSession;
      this.dateLocale = langSession;
      this.dateAdapter.setLocale(langSession);
    } else {
      this.i18NextService.changeLanguage(language);
      document.documentElement.lang = language;
      sessionStorage.setItem('i18nlang', language);
    }
    this.addScriptCookieBot(langSession || language);
  }

  private addScriptCookieBot(language: string): void {
    const script = document.createElement('script');
    script.id = 'Cookiebot';
    script.src = 'https://consent.cookiebot.com/uc.js';
    script.dataset.cbid = 'fa2eae20-4a21-404b-a849-1f743a97adf3';
    script.dataset.blockingmode = 'ignore';
    script.dataset.culture = language === 'es-ES' ? 'es' : 'pt';
    script.type = 'text/javascript';
    const script2 = document.createElement('script');
    script2.src = '../../assets/cookiebot/cookiebot.js';
    script2.type = 'text/javascript';
    const link = document.createElement('link');
    link.href = '../../assets/cookiebot/cookiebot.css';
    link.rel = 'stylesheet';
    this.document.head.appendChild(script);
    this.document.head.appendChild(script2);
    this.document.head.appendChild(link);
  }
}
